import { useNavigate, useLocation } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import Layout from "../components/Layout";
import { useApiStore } from "../store";
import useI18n from "../hooks/useI18n";
import usePageMetadata from "../hooks/usePageMetadata";
import useAuthorizedPages from "../hooks/useAuthorizedPages";
import useIsRoleAdmin from "../hooks/useIsRoleAdmin";
import metadata from "../data/site.config.json";

const ChangePasswordScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { defaultPage } = usePageMetadata();
  const { authorizedPages } = useAuthorizedPages();
  const currentIsRoleAdmin = useIsRoleAdmin();

  const { showModal, changePassword, respondToAuthChallenge } = useApiStore(
    (state) => state
  );

  const forceChange = location.state?.forceChange;
  const usernameFromState = location.state?.username;
  const sessionFromState = location.state?.session;

  // Removed useEffect that cleared local storage.
  // We want authenticated users to stay logged in when visiting this page.

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = data.get("email")?.toString() || "";
    const oldPassword = data.get("oldPassword")?.toString() || "";
    const newPassword = data.get("newPassword")?.toString() || "";
    const confirmPassword = data.get("confirmPassword")?.toString() || "";

    if (newPassword !== confirmPassword) {
      showModal(t("form.data_validation"), t("form.passwords_do_not_match"));
      return;
    }

    let authenticated = false;
    let isRoleAdmin = false;

    if (forceChange) {
      const result = await respondToAuthChallenge(
        email, // email field acts as username here
        newPassword,
        sessionFromState
      );
      authenticated = result.authenticated;
      isRoleAdmin = result.isRoleAdmin || false;
    } else {
      authenticated = await changePassword(email, oldPassword, newPassword);
      // For regular password change, reuse the current admin flag
      isRoleAdmin = currentIsRoleAdmin;
    }

    if (authenticated) {
      // For forceChange flow, read fresh authorizedPages from localStorage
      // since the hook value was read before authentication
      const authorized_pages_key = `${metadata.localDB.name}_authorized_pages`;
      const freshAuthorizedPagesString = localStorage.getItem(authorized_pages_key);
      const freshAuthorizedPages = freshAuthorizedPagesString
        ? JSON.parse(freshAuthorizedPagesString)
        : [];

      const pagesForNavigation = forceChange ? freshAuthorizedPages : authorizedPages;
      const authorizedDefaultPage =
        pagesForNavigation.includes(defaultPage) || pagesForNavigation.length === 0
          ? defaultPage
          : pagesForNavigation[0];
      const navigationDestination = isRoleAdmin
        ? `/${authorizedDefaultPage}`
        : `/${authorizedDefaultPage}/form`;

      navigate(navigationDestination);
    }
  };

  return (
    <Layout showMenu={!forceChange}>
      <Container component="main" sx={{ maxWidth: { xs: "sm", md: "sm" } }}>
        <Paper
          sx={{
            marginTop: 8,
            padding: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockOpenIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            {forceChange
              ? t("auth.set_new_password")
              : t("auth.change_password")}
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t("auth.username")}
              name="email"
              autoComplete="email"
              defaultValue={usernameFromState || ""}
              InputProps={{
                readOnly: !!usernameFromState,
              }}
              autoFocus={!usernameFromState}
            />
            {!forceChange && (
              <TextField
                margin="normal"
                required
                fullWidth
                name="oldPassword"
                label={t("auth.old_password")}
                type="password"
                id="oldPassword"
              />
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label={t("auth.new_password")}
              type="password"
              id="newPassword"
              autoComplete="off"
              autoFocus={!!usernameFromState}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label={t("auth.confirm_password")}
              type="password"
              id="confirmPassword"
              autoComplete="off"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              size="large"
            >
              {t("form.proceed")}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Layout>
  );
};

export default ChangePasswordScreen;
